import { X } from 'lucide-react';
import {
  type Rule,
  type FieldDef,
  type Operator,
  OPERATOR_LABELS,
  operatorsForField,
  isArrayOperator,
  makeRule,
} from './dsl-utils.ts';

interface Props {
  rule: Rule;
  fields: FieldDef[];
  onChange: (rule: Rule) => void;
  onRemove: () => void;
}

// ─── Value input — adapts to field type and operator ──────────────────────────

function ValueInput({
  rule,
  field,
  onChange,
}: {
  rule: Rule;
  field: FieldDef | undefined;
  onChange: (value: Rule['value']) => void;
}) {
  const fieldType = field?.fieldType ?? 'text';
  const op = rule.operator;

  const inputClass =
    'h-8 px-2.5 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-shadow min-w-0 w-full';

  // Boolean: true / false select
  if (fieldType === 'boolean') {
    return (
      <select
        className={inputClass}
        value={String(rule.value)}
        onChange={(e) => onChange(e.target.value === 'true')}
      >
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }

  // Number
  if (fieldType === 'number') {
    return (
      <input
        type="number"
        className={inputClass}
        value={rule.value as number}
        onChange={(e) => onChange(Number(e.target.value))}
        placeholder="0"
      />
    );
  }

  // Date
  if (fieldType === 'date') {
    return (
      <input
        type="date"
        className={inputClass}
        value={rule.value as string}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  // Select field with eq/neq: render a <select> with options
  if (fieldType === 'select' && !isArrayOperator(op) && field?.options?.length) {
    return (
      <select
        className={inputClass}
        value={rule.value as string}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— choose —</option>
        {field.options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }

  // Array operator (in / not_in): comma-separated tags
  if (isArrayOperator(op)) {
    const arr = Array.isArray(rule.value) ? rule.value : [];
    return (
      <div className="flex-1 min-w-0">
        <input
          type="text"
          className={inputClass}
          value={arr.join(', ')}
          onChange={(e) => {
            const parts = e.target.value
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
            onChange(parts);
          }}
          placeholder="value1, value2, …"
        />
      </div>
    );
  }

  // Default: text input
  return (
    <input
      type="text"
      className={inputClass}
      value={rule.value as string}
      onChange={(e) => onChange(e.target.value)}
      placeholder="value"
    />
  );
}

// ─── RuleRow ──────────────────────────────────────────────────────────────────

export function RuleRow({ rule, fields, onChange, onRemove }: Props) {
  const field = fields.find((f) => f.key === rule.field);
  const fieldType = field?.fieldType ?? 'text';
  const availableOps = operatorsForField(fieldType);

  const handleFieldChange = (key: string) => {
    const def = fields.find((f) => f.key === key);
    onChange(makeRule(key, def?.fieldType ?? 'text'));
  };

  const handleOperatorChange = (op: Operator) => {
    // Reset value when switching between array/non-array operators
    const wasArray = isArrayOperator(rule.operator);
    const willBeArray = isArrayOperator(op);
    let value = rule.value;
    if (wasArray && !willBeArray) value = '';
    if (!wasArray && willBeArray) value = [];
    onChange({ ...rule, operator: op, value });
  };

  const selectClass =
    'h-8 px-2 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-shadow cursor-pointer';

  return (
    <div className="flex items-center gap-2 group/row">
      {/* Field */}
      <select
        className={`${selectClass} min-w-[130px]`}
        value={rule.field}
        onChange={(e) => handleFieldChange(e.target.value)}
      >
        {/* Standard group */}
        <optgroup label="Standard">
          {fields
            .filter((f) => f.isStandard)
            .map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
        </optgroup>
        {/* Custom group — only if any */}
        {fields.some((f) => !f.isStandard) && (
          <optgroup label="Custom attributes">
            {fields
              .filter((f) => !f.isStandard)
              .map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
          </optgroup>
        )}
      </select>

      {/* Operator */}
      <select
        className={`${selectClass} min-w-[110px]`}
        value={rule.operator}
        onChange={(e) => handleOperatorChange(e.target.value as Operator)}
      >
        {availableOps.map((op) => (
          <option key={op} value={op}>
            {OPERATOR_LABELS[op]}
          </option>
        ))}
      </select>

      {/* Value */}
      <div className="flex-1 min-w-[100px]">
        <ValueInput rule={rule} field={field} onChange={(v) => onChange({ ...rule, value: v })} />
      </div>

      {/* Remove */}
      <button
        type="button"
        onClick={onRemove}
        className="p-1 text-zinc-300 hover:text-red-400 hover:bg-red-50 rounded transition-colors shrink-0"
        title="Remove condition"
      >
        <X size={13} />
      </button>
    </div>
  );
}
